import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error(el ? 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες' : 'Password must be at least 8 characters');
            return;
        }
        if (password !== confirm) {
            toast.error(el ? 'Οι κωδικοί δεν ταιριάζουν' : 'Passwords do not match');
            return;
        }
        setLoading(true);
        try {
            await api.post('/auth/reset-password', { token, new_password: password });
            toast.success(el ? 'Ο κωδικός άλλαξε! Συνδεθείτε με τον νέο κωδικό.' : 'Password updated! Sign in with your new password.');
            navigate('/login');
        } catch (error) {
            toast.error(error.response?.data?.detail || (el ? 'Ο σύνδεσμος δεν είναι έγκυρος' : 'Invalid reset link'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card" data-testid="reset-password-page">
                <div className="auth-logo">
                    <Link to="/">
                        <img
                            src="https://customer-assets-agu9un31.emergentagent.net/job_fleet-ops-62/artifacts/sxi4lya9_%CE%91%CE%BD%CF%89%CC%81%CE%BD%CF%85%CE%BC%CE%BF%20%CF%83%CF%87%CE%B5%CC%81%CE%B4%CE%B9%CE%BF.webp"
                            alt="Cofleet"
                            style={{ width: '200px', height: 'auto' }}
                            className="object-contain mx-auto mb-4 hover:opacity-80 transition-opacity"
                        />
                    </Link>
                    <p className="auth-subtitle">{el ? 'Ορίστε νέο κωδικό πρόσβασης' : 'Set a new password'}</p>
                </div>

                {!token ? (
                    <div className="text-center space-y-4">
                        <p className="text-sm text-red-500">
                            {el ? 'Λείπει ο σύνδεσμος επαναφοράς. Ζητήστε νέον.' : 'Reset token missing. Please request a new link.'}
                        </p>
                        <Link to="/forgot-password" className="text-sm text-[#1B7EB5] hover:text-[#58A6B4]">
                            {el ? 'Ζήτηση νέου συνδέσμου' : 'Request a new link'}
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                        <div className="form-group">
                            <Label className="form-label">{el ? 'Νέος Κωδικός' : 'New Password'}</Label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={el ? 'Τουλάχιστον 8 χαρακτήρες' : 'At least 8 characters'}
                                    className="bg-white border-slate-300 text-slate-900 pr-10"
                                    required
                                    data-testid="reset-new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                    data-testid="toggle-reset-password-visibility"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">{el ? 'Επιβεβαίωση Κωδικού' : 'Confirm Password'}</Label>
                            <Input
                                type={showPassword ? 'text' : 'password'}
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                placeholder={el ? 'Επαναλάβετε τον κωδικό' : 'Repeat the password'}
                                className="bg-white border-slate-300 text-slate-900"
                                required
                                data-testid="reset-confirm-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full btn-primary h-11"
                            disabled={loading}
                            data-testid="reset-submit-btn"
                        >
                            {loading
                                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{el ? 'Αποθήκευση...' : 'Saving...'}</>
                                : (el ? 'Αλλαγή κωδικού' : 'Change password')}
                        </Button>
                        <div className="text-center">
                            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#1B7EB5] hover:text-[#58A6B4] transition-colors">
                                <ArrowLeft className="w-4 h-4" />
                                {el ? 'Επιστροφή στη σύνδεση' : 'Back to sign in'}
                            </Link>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
